import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Modal from "@/components/ui/modal";
import Button from "@/components/ui/button";
import Dropdown from "@/components/ui/dropdown";
import { Shield, Close as CloseIcon } from "@/components/icons";
import type { Staff } from "../../types/staff-management.types";
import { staffManagementApi } from "../../services/staff-management.api";
import { rolesAndPermissionsApi } from "@/features/roles-and-permissions/services/roles-and-permissions.api";
import type { Role } from "@/features/roles-and-permissions/types/roles-and-permissions.types";
import styles from "./AssignRoleModal.module.scss";

interface AssignRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  staff: Staff | null;
  onConfirm?: (staffId: string, roleIds: string[]) => void;
}

const AssignRoleModal: React.FC<AssignRoleModalProps> = ({
  isOpen,
  onClose,
  staff,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [_, setUserRoleIds] = useState<string[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const roleOptions = allRoles.map((r) => ({
    label: r.roleName ?? r.id,
    value: r.id,
  }));

  useEffect(() => {
    if (!isOpen) return;

    const load = async () => {
      setIsLoadingRoles(true);
      try {
        const [rolesRes, currentRoles] = await Promise.all([
          rolesAndPermissionsApi.getAll({ pageSize: 500 }),
          staff ? staffManagementApi.getRoles(staff.id) : [],
        ]);
        setAllRoles(rolesRes.data);
        const ids = currentRoles.map((r) => r.id);
        setUserRoleIds(ids);
        setSelectedRoleIds(ids);
      } catch {
        setAllRoles([]);
        setUserRoleIds([]);
        setSelectedRoleIds([]);
      } finally {
        setIsLoadingRoles(false);
      }
    };
    load();
  }, [isOpen, staff?.id]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!staff || !onConfirm) return;
    if (selectedRoleIds.length === 0) {
      toast.error(
        t("staffManagement.assignRoleModal.atLeastOneRole"),
      );
      return;
    }

    setIsSubmitting(true);
    try {
      await staffManagementApi.assignRole(staff.id, selectedRoleIds);
      onConfirm(staff.id, selectedRoleIds);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const customHeader = (
    <div className={styles.modalHeader}>
      <div className={styles.modalHeaderLeft}>
        <Shield width={24} height={24} color="#14181F" />
        <h3 className={styles.modalTitle}>
          {t("staffManagement.assignRoleModal.title")}
        </h3>
      </div>
      <button
        type="button"
        className={styles.closeButton}
        onClick={onClose}
        aria-label={t("common.close")}
      >
        <CloseIcon width={20} height={20} color="#72808f" />
      </button>
    </div>
  );

  const customFooter = (
    <div className={styles.footerButtons}>
      <Button
        variant="text"
        color="secondary"
        type="button"
        onClick={onClose}
        disabled={isSubmitting}
      >
        {t("staffManagement.assignRoleModal.cancel")}
      </Button>
      <Button
        variant="contained"
        color="primary"
        type="submit"
        form="assignRoleForm"
        disabled={isSubmitting || selectedRoleIds.length === 0}
        loading={isSubmitting}
      >
        {t("staffManagement.assignRoleModal.save")}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("staffManagement.assignRoleModal.title")}
      customHeader={customHeader}
      customFooter={customFooter}
      size="md"
      withForm
      formId="assignRoleForm"
      handleSubmit={handleSubmit}
    >
      <div className={styles.content}>
        {staff && (
          <p className={styles.staffName}>
            {t("staffManagement.assignRoleModal.forUser")}:{" "}
            <strong>{staff.name}</strong>
          </p>
        )}
        <div className={styles.formField}>
          <Dropdown
            label={t("staffManagement.assignRoleModal.rolesLabel")}
            placeholder={t("staffManagement.assignRoleModal.rolesPlaceholder")}
            options={roleOptions}
            value={selectedRoleIds}
            multiple
            searchable
            menuPosition="bottom"
            selectedLabel={
              selectedRoleIds.length > 0
                ? selectedRoleIds
                    .map(
                      (id) =>
                        roleOptions.find((o) => o.value === id)?.label ?? id,
                    )
                    .join(", ")
                : undefined
            }
            onSelect={(value) =>
              setSelectedRoleIds(Array.isArray(value) ? value : value ? [value] : [])
            }
            disabled={isLoadingRoles}
          />
        </div>
      </div>
    </Modal>
  );
};

export default AssignRoleModal;
